import React, { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import Imgix from 'react-imgix'
import { Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'

export const FACULTY_ROW_DND = 'FACULTY_ROW'

const FacultyCard = ({
  faculty,
  index,
  onEdit,
  onDelete,
  moveItem,
  onDragEnd,
  isUploading,
  isEditing = false,
}) => {
  const ref = useRef(null)

  const [{ handlerId }, drop] = useDrop({
    accept: FACULTY_ROW_DND,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current || isUploading) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: FACULTY_ROW_DND,
    item: () => ({ index }),
    canDrag: !isUploading,
    end() {
      if (!isUploading && typeof onDragEnd === 'function') {
        onDragEnd()
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const handleCardClick = () => {
    onEdit(faculty, index)
  }

  const handleActionClick = (e, action) => {
    e.stopPropagation()
    action()
  }

  const subtitle = [faculty.title, faculty.expertise]
    .filter(Boolean)
    .join(' · ')
  const imageNote = faculty.imageFileName ? '' : ' · no image yet'

  return (
    <div
      ref={ref}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={classNames('experience-fac', {
        'is-editing': isEditing,
        'is-dragging': isDragging,
      })}
      data-handler-id={handlerId}
      onClick={handleCardClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleCardClick()
        }
      }}>
      <span
        className='experience-grab'
        title='Reorder'
        onClick={(e) => e.stopPropagation()}>
        ⋮⋮
      </span>
      {faculty.imageFileName ? (
        faculty.imageFileName.startsWith('data:') ? (
          <img
            src={faculty.imageFileName}
            alt=''
            className='experience-fac-av'
          />
        ) : (
          <Imgix
            imgixParams={{
              fit: 'crop',
              crop: 'faces',
              auto: 'format,compress',
              q: 90,
            }}
            src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${faculty.imageFileName}`}
            width={42}
            height={42}
            alt=''
            className='experience-fac-av lazyload'
          />
        )
      ) : (
        <div className='experience-fac-av is-empty' aria-hidden='true' />
      )}
      <div className='experience-fac-copy'>
        <b>{faculty.name || 'Unnamed Faculty'}</b>
        <span>
          {subtitle || 'No title yet'}
          {imageNote}
        </span>
      </div>
      <button
        type='button'
        className='experience-x'
        title='Delete'
        aria-label='Delete'
        disabled={isUploading}
        onClick={(e) => handleActionClick(e, onDelete)}>
        {isUploading ? (
          <Spinner animation='border' size='sm' />
        ) : (
          <ExperienceDeleteIcon />
        )}
      </button>
    </div>
  )
}

export default FacultyCard
